import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import RoomIcon from "@mui/icons-material/Room";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import DashboardLayout from "../../layouts/DashboardLayout";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import type { MRT_ColumnDef } from "material-react-table";
import type { Shipment, ShipmentStatus, OrderSnapshot, ItemSnapshot, Order } from "../../types/DB2";
import { formatStatusLabel } from "../../utils/status";
import {
  collectOrderDisplayData,
  compareByCabinet,
  getOrderStatus,
  OrderDisplayData,
  stripOrderPrefix,
} from "../../utils/ordersDisplay";
import { useShipmentManager } from "../../context/ShipmentManagerProvider";
import { useUsersByManufacturer } from "../../context/UsersDataFetcher";
import { useUserData } from "../../context/UserDataFetcher";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import styled from "@emotion/styled";
import { CustomThemeType } from "../../context/Providers";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { format } from "date-fns";
import ShipmentsMap, { MapShipment } from "../../components/ShipmentsMap/ShipmentsMap";
import { darkMapStyle, lightMapStyle } from "../../components/ShipmentsMap/mapStyles";
import { getAddressCandidates, useShipmentGeocodes } from "../../utils/geocode";
import {
  getItemStatusChipColor,
  getItemStatusMarkerIcon,
  getOrderStatusChipColor,
  getOrderStatusMarkerIcon,
  getShipmentStatusChipColor,
  getShipmentStatusMarkerIcon,
  normalizeStatusValue,
} from "../../utils/statusColors";
import { useThemes } from "../../context/ThemesProvider";

const CalendarWrapper = styled.div<{ theme?: CustomThemeType }>`
  & .rdrDefinedRangesWrapper {
    display: none;
  }
  & .rdrCalendarWrapper {
    background-color: ${({ theme }) => theme?.background};
    border: 2px solid ${({ theme }) => theme?.dividerColor};
  }
  & .rdrDateDisplayWrapper {
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrStartEdge,
  & .rdrInRange,
  & .rdrEndEdge,
  & .rdrSelected {
    color: ${({ theme }) => theme?.background} !important;
  }
  & .rdrDayNumber span {
    color: ${({ theme }) => theme?.foreground};
  }
  & .rdrDayNumber span::after {
    background: ${({ theme }) => theme?.foreground};
  }
  & .rdrMonthName,
  & .rdrWeekDay {
    color: ${({ theme }) => theme?.brand};
  }
  & .rdrDay {
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrNextPrevButton {
    color: ${({ theme }) => theme?.foreground};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrNextPrevButton i {
    color: ${({ theme }) => theme?.foreground};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrMonthAndYearPickers select {
    color: ${({ theme }) => theme?.brand};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrPprevButton i {
    border-color: transparent ${({ theme }) => theme?.brand} transparent
      transparent;
  }
  & .rdrNextButton i {
    border-color: transparent transparent transparent
      ${({ theme }) => theme?.brand};
  }
  & .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
    color: ${({ theme }) => theme?.foreground};
  }
`;

type GeoPointLike = { latitude?: number; longitude?: number } | null | undefined;

type ItemHistoryEntry = {
  at?: unknown;
  location?: string;
  byUserId?: string;
  statusBeforeScan?: string;
  statusAfterScan?: string;
  notes?: string;
  shipmentId?: string;
  geo?: GeoPointLike;
};

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  created: "Created",
  dispatched: "Dispatched",
  inTransit: "In Transit",
  arrived: "Arrived",
  completed: "Completed",
  delayed: "Delayed",
};

const getShipmentDate = (shipment: Shipment): Date | null => {
  const raw = shipment.shipmentDate as unknown;
  if (!raw) return null;
  if (typeof (raw as any)?.toDate === "function") {
    return (raw as any).toDate();
  }
  try {
    return new Date(raw as any);
  } catch {
    return null;
  }
};

const coerceToDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (typeof (value as any)?.toDate === "function") {
    try {
      return (value as any).toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === "number") {
    return new Date(value);
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const toLatLng = (geo: GeoPointLike): { lat: number; lng: number } | null => {
  if (!geo) return null;
  const lat = typeof geo.latitude === "number" ? geo.latitude : undefined;
  const lng = typeof geo.longitude === "number" ? geo.longitude : undefined;
  if (typeof lat === "number" && typeof lng === "number") {
    return { lat, lng };
  }
  return null;
};

const resolveOrderLocation = (
  order: OrderDisplayData,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string }
): { lat?: number; lng?: number; formattedAddress?: string } => {
  const destinationsGeo =
    (order.snapshot?.destinationsGeo
      ? Object.values(order.snapshot.destinationsGeo)
      : []) ??
    [];
  const preservedDestinationsGeo =
    (order.snapshot?.preservedOrder?.destinationsGeo
      ? Object.values(order.snapshot.preservedOrder.destinationsGeo)
      : []) ?? [];

  const geoCandidates = [...destinationsGeo, ...preservedDestinationsGeo];
  for (const geo of geoCandidates) {
    const latLng = toLatLng(geo as GeoPointLike);
    if (latLng) return latLng;
  }

  for (const item of order.includedItems) {
    const latLng = toLatLng((item as any)?.dropoffGeo as GeoPointLike);
    if (latLng) return { ...latLng };
  }

  if (shipmentLocation) {
    return {
      lat: shipmentLocation.lat,
      lng: shipmentLocation.lng,
      formattedAddress: shipmentLocation.formattedAddress,
    };
  }

  return {};
};

const resolveItemLocation = (
  item: ItemSnapshot,
  order: OrderDisplayData,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string }
): { lat?: number; lng?: number; formattedAddress?: string } => {
  const itemLatLng = toLatLng((item as any)?.dropoffGeo as GeoPointLike);
  if (itemLatLng) {
    return itemLatLng;
  }

  const orderLocation = resolveOrderLocation(order, shipmentLocation);
  if (orderLocation.lat && orderLocation.lng) {
    return orderLocation;
  }

  if (shipmentLocation) {
    return {
      lat: shipmentLocation.lat,
      lng: shipmentLocation.lng,
      formattedAddress: shipmentLocation.formattedAddress,
    };
  }

  return {};
};

const buildShipmentDialogData = (
  shipment: Shipment,
  orders: OrderDisplayData[],
  location?: { lat: number; lng: number; formattedAddress?: string }
) => {
  const shipmentDate = getShipmentDate(shipment);
  const totalItems = orders.reduce(
    (sum, order) => sum + order.includedItems.length + order.excludedItems.length,
    0
  );
  const uniqueDestinations = Array.from(
    new Set(orders.flatMap((order) => order.destinations))
  );
  const addressFallback = getAddressCandidates(shipment).find((candidate) => candidate.trim());
  const mapShipment: MapShipment = {
    id: shipment.id,
    status: shipment.status,
    label: shipment.id || "Shipment",
    lat: location?.lat,
    lng: location?.lng,
    iconUrl: getShipmentStatusMarkerIcon(shipment.status),
    subtitle: shipmentDate ? format(shipmentDate, "PP") : undefined,
    addressSummary: location?.formattedAddress || addressFallback,
    detailLines: [
      `Status: ${
        STATUS_LABELS[shipment.status] ||
        formatStatusLabel(shipment.status) ||
        shipment.status
      }`,
      `Orders: ${orders.length}`,
      `Items (incl./excl.): ${orders.reduce(
        (acc, order) => acc + order.includedItems.length,
        0
      )}/${orders.reduce((acc, order) => acc + order.excludedItems.length, 0)}`,
      `Unique destinations: ${uniqueDestinations.length}`,
    ],
    orders: orders.map((order) => ({
      orderId: order.orderId,
      itemCount: order.includedItems.length,
      destinations: order.destinations,
    })),
  };

  return {
    mapShipment,
    totalItems,
    uniqueDestinations,
    shipmentDate,
    primaryAddress: mapShipment.addressSummary,
    driverList: shipment.drivers || [],
  };
};

const buildOrderDialogData = (
  shipment: Shipment,
  order: OrderDisplayData,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string }
) => {
  const orderLocation = resolveOrderLocation(order, shipmentLocation);
  const shipmentDate = getShipmentDate(shipment);
  const addressSummary =
    orderLocation.formattedAddress ||
    order.destinations[0] ||
    shipmentLocation?.formattedAddress;
  const orderStatus = getOrderStatus(order);

  const mapShipment: MapShipment = {
    id: `${shipment.id}-${order.orderId}`,
    status: shipment.status,
    label: `Order ${stripOrderPrefix(order.orderId, order.snapshot)}`,
    lat: orderLocation.lat,
    lng: orderLocation.lng,
    iconUrl: getOrderStatusMarkerIcon(orderStatus),
    subtitle: shipmentDate ? `Shipment date: ${format(shipmentDate, "PP")}` : undefined,
    addressSummary,
    detailLines: [
      `Shipment: ${shipment.id}`,
      `Dealer: ${order.dealerLabel}`,
      `Included items: ${order.includedItems.length}`,
      `Excluded items: ${order.excludedItems.length}`,
      `Destinations: ${order.destinations.length || "None"}`,
    ],
    orders: [
      {
        orderId: order.orderId,
        itemCount: order.includedItems.length,
        destinations: order.destinations,
      },
    ],
  };

  return {
    mapShipment,
    addressSummary,
  };
};

const buildItemDialogData = (
  shipment: Shipment,
  order: OrderDisplayData,
  item: ItemSnapshot,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string }
) => {
  const itemLocation = resolveItemLocation(item, order, shipmentLocation);
  const shipmentDate = getShipmentDate(shipment);
  const cabinetLabel = item.cabinetNumber ? String(item.cabinetNumber).trim() : "";
  const itemLabel = cabinetLabel.length
    ? `Cabinet ${cabinetLabel}`
    : item.modelName || item.id;
  const addressSummary =
    itemLocation.formattedAddress ||
    item.destination ||
    order.destinations[0] ||
    shipmentLocation?.formattedAddress;

  const mapShipment: MapShipment = {
    id: `${shipment.id}-${order.orderId}-${item.id}`,
    status: shipment.status,
    label: itemLabel,
    lat: itemLocation.lat,
    lng: itemLocation.lng,
    iconUrl: getItemStatusMarkerIcon(item.status),
    subtitle: shipmentDate ? `Shipment date: ${format(shipmentDate, "PP")}` : undefined,
    addressSummary,
    detailLines: [
      `Shipment: ${shipment.id}`,
      `Order: ${order.orderId}`,
      `Item status: ${
        formatStatusLabel(item.status) || item.status || "Unknown"
      }`,
      cabinetLabel.length ? `Cabinet #: ${cabinetLabel}` : undefined,
      item.destination ? `Destination: ${item.destination}` : undefined,
    ].filter(Boolean) as string[],
    orders: [
      {
        orderId: order.orderId,
        itemCount: 1,
        destinations: order.destinations,
      },
    ],
  };

  return {
    mapShipment,
    addressSummary,
  };
};

type MapDialogContext =
  | { kind: "shipment"; shipment: Shipment }
  | { kind: "order"; shipment: Shipment; order: OrderDisplayData; statusKey?: string }
  | {
      kind: "item";
      shipment: Shipment;
      order: OrderDisplayData;
      item: ItemSnapshot;
      itemType: "included" | "excluded";
      statusKey?: string;
    };

type MapDialogState =
  | { open: false }
  | { open: true; context: MapDialogContext };

type ShipmentDialogData = {
  kind: "shipment";
  title: string;
  subtitle?: string;
  mapShipments: MapShipment[];
  shipment: Shipment;
  orders: OrderDisplayData[];
  totalItems: number;
  uniqueDestinations: string[];
  primaryAddress?: string;
  driverList: string[];
};

type OrderDialogData = {
  kind: "order";
  title: string;
  subtitle?: string;
  mapShipments: MapShipment[];
  shipment: Shipment;
  order: OrderDisplayData;
  addressSummary?: string;
  statusKey?: string;
};

type ItemDialogData = {
  kind: "item";
  title: string;
  subtitle?: string;
  mapShipments: MapShipment[];
  shipment: Shipment;
  order: OrderDisplayData;
  item: ItemSnapshot;
  addressSummary?: string;
  itemType: "included" | "excluded";
  history: ItemHistoryEntry[];
  statusKey?: string;
};

type ActiveDialogData = ShipmentDialogData | OrderDialogData | ItemDialogData;

type ShipmentInfoPanelProps = {
  data: ShipmentDialogData;
  driverNameLookup: Record<string, string>;
  onOpenOrderMap?: (order: OrderDisplayData) => void;
};

const ShipmentInfoPanel: React.FC<ShipmentInfoPanelProps> = ({
  data,
  driverNameLookup,
  onOpenOrderMap,
}) => {
  const driverNames = data.driverList
    .map((driverId) => driverNameLookup[driverId] || driverId)
    .filter((name) => name && name.trim().length > 0);
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Shipment Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.primaryAddress || "No primary address available"}
        </Typography>
      </Box>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            size="small"
            color={getShipmentStatusChipColor(data.shipment.status)}
            label={
              STATUS_LABELS[data.shipment.status] ||
              formatStatusLabel(data.shipment.status) ||
              data.shipment.status
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Orders: ${data.orders.length}`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Items: ${data.totalItems}`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Destinations: ${data.uniqueDestinations.length}`}
          />
        </Stack>
        {data.primaryAddress && (
          <Stack direction="row" spacing={1} alignItems="center">
            <RoomIcon fontSize="small" color="primary" />
            <Typography variant="body2">{data.primaryAddress}</Typography>
          </Stack>
        )}
        {driverNames.length ? (
          <Typography variant="body2">
            Drivers: {driverNames.join(", ")}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No drivers assigned.
          </Typography>
        )}
      </Stack>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Orders
        </Typography>
        <List dense disablePadding>
          {data.orders.map((order) => {
            const destinationsLabel = order.destinations.length
              ? order.destinations.join(", ")
              : "No destinations recorded";
            const action = onOpenOrderMap
              ? () => onOpenOrderMap(order)
              : undefined;
            return (
              <ListItem
                key={order.orderId}
                disableGutters
                secondaryAction={
                  action ? (
                    <Tooltip title="Open map view">
                      <IconButton edge="end" size="small" onClick={action}>
                        <RoomIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Order {stripOrderPrefix(order.orderId, order.snapshot)}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${order.includedItems.length} items`}
                      />
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Destinations: {destinationsLabel}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
          {!data.orders.length && (
            <Typography variant="body2" color="text.secondary">
              No orders attached to this shipment.
            </Typography>
          )}
        </List>
      </Box>
    </Stack>
  );
};

type OrderInfoPanelProps = {
  data: OrderDialogData;
  onOpenItemMap?: (item: ItemSnapshot, itemType: "included" | "excluded") => void;
};

const OrderInfoPanel: React.FC<OrderInfoPanelProps> = ({ data, onOpenItemMap }) => {
  const destinationsLabel = data.order.destinations.length
    ? data.order.destinations.join(", ")
    : "No destinations recorded";
  const orderStatus = getOrderStatus(data.order);
  const orderStatusLabel =
    formatStatusLabel(orderStatus) || orderStatus || "Unknown";
  const orderStatusChipColor = getOrderStatusChipColor(orderStatus);

  const renderItems = (
    items: ItemSnapshot[],
    heading: string,
    itemType: "included" | "excluded"
  ) => (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {heading}
      </Typography>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem
            key={item.id}
            disableGutters
            secondaryAction={
              onOpenItemMap ? (
                <Tooltip title="Open map view">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => onOpenItemMap(item, itemType)}
                  >
                    <RoomIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null
            }
            sx={{ alignItems: "flex-start", py: 0.75 }}
          >
            <Inventory2Icon
              fontSize="small"
              sx={{ color: "text.secondary", mr: 1, mt: 0.5 }}
            />
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  {item.cabinetNumber && (
                    <Typography variant="body2" fontWeight={600}>
                      Cabinet # {item.cabinetNumber}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={formatStatusLabel(item.status) || item.status || ""}
                    color={getItemStatusChipColor(item.status)}
                  />
                  <Typography variant="body2">
                    {item.modelName || item.description || item.id}
                  </Typography>
                </Stack>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" display="block">
                  Destination: {item.destination || "Not provided"}
                </Typography>
              }
            />
          </ListItem>
        ))}
        {!items.length && (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 4, py: 0.5 }}>
            No items in this bucket.
          </Typography>
        )}
      </List>
    </Box>
  );

  return (
    <Stack spacing={2}>
      <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Order {stripOrderPrefix(data.order.orderId, data.order.snapshot)}
          </Typography>
        <Chip
          size="small"
          color={orderStatusChipColor}
          label={orderStatusLabel}
          sx={{ mt: 0.5 }}
        />
        <Typography variant="body2" color="text.secondary">
          Dealer: {data.order.dealerLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Destinations: {destinationsLabel}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          label={`Included: ${data.order.includedItems.length}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`Excluded: ${data.order.excludedItems.length}`}
        />
      </Stack>
      <Divider />
      {renderItems(data.order.includedItems, "Included Items", "included")}
      <Divider />
      {renderItems(data.order.excludedItems, "Excluded Items", "excluded")}
    </Stack>
  );
};

type ItemInfoPanelProps = {
  data: ItemDialogData;
};

const ItemInfoPanel: React.FC<ItemInfoPanelProps> = ({ data }) => {
  const history = [...data.history].sort((a, b) => {
    const dateA = coerceToDate(a.at)?.getTime() ?? 0;
    const dateB = coerceToDate(b.at)?.getTime() ?? 0;
    return dateB - dateA;
  });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {data.item.modelName || data.item.description || data.item.id}
        </Typography>
        {/* Item ID removed from UI per request */}
      </Box>
      <Stack spacing={1}>
        <Chip
          size="small"
          color={getItemStatusChipColor(data.item.status)}
          label={`Status: ${
            formatStatusLabel(data.item.status) || data.item.status || ""
          }`}
        />
        {data.item.cabinetNumber && (
          <Typography variant="body2">
            Cabinet #: {data.item.cabinetNumber}
          </Typography>
        )}
        <Typography variant="body2">
          Destination: {data.item.destination || "Not provided"}
        </Typography>
        <Typography variant="body2">
          Order: {stripOrderPrefix(data.order.orderId, data.order.snapshot)}
        </Typography>
        <Typography variant="body2">
          Shipment: {data.shipment.id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.addressSummary || "No address available"}
        </Typography>
      </Stack>
      <Divider />
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HistoryIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Scan History
          </Typography>
        </Stack>
        {history.length ? (
          <List dense disablePadding>
            {history.map((entry, idx) => {
              const timestamp = coerceToDate(entry.at);
              const timestampLabel = timestamp
                ? format(timestamp, "PP p")
                : "Unknown time";
              return (
                <ListItem key={idx} disableGutters sx={{ py: 0.75 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {timestampLabel}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Location: {entry.location || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Status:{" "}
                          {formatStatusLabel(entry.statusBeforeScan) ||
                            entry.statusBeforeScan ||
                            ""}{" "}
                          →{" "}
                          {formatStatusLabel(entry.statusAfterScan) ||
                            entry.statusAfterScan ||
                            ""}
                        </Typography>
                        {entry.notes && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Notes: {entry.notes}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No scan history recorded for this item.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

interface OrdersDetailPanelProps {
  shipment: Shipment;
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string };
  onOpenOrderMap?: (payload: { shipment: Shipment; order: OrderDisplayData }) => void;
  onOpenItemMap?: (payload: {
    shipment: Shipment;
    order: OrderDisplayData;
    item: ItemSnapshot;
    itemType: "included" | "excluded";
  }) => void;
}

const OrdersDetailPanel: React.FC<OrdersDetailPanelProps> = ({
  shipment,
  shipmentLocation,
  onOpenOrderMap,
  onOpenItemMap,
}) => {
  const muiTheme = useMuiTheme();
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {}
  );

  const orders = useMemo(() => {
    const computed = collectOrderDisplayData(shipment);
    console.log("[ViewShipments] OrdersDetailPanel computed orders", {
      shipmentId: shipment.id,
      orderCount: computed.length,
    });
    return computed;
  }, [shipment]);

  const handleToggleOrder = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleOrderMapClick = (order: OrderDisplayData) => {
    onOpenOrderMap?.({ shipment, order });
  };

  const handleItemMapClick = (
    order: OrderDisplayData,
    item: ItemSnapshot,
    itemType: "included" | "excluded"
  ) => {
    onOpenItemMap?.({ shipment, order, item, itemType });
  };

  if (!orders.length) {
    return (
      <Box sx={{ py: 2, px: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No orders attached to this shipment yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ py: 1 }}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <List disablePadding>
        {orders.map((order) => {
          const totalItems = order.includedItems.length + order.excludedItems.length;
          const destinationsLabel = order.destinations.length
            ? order.destinations.join(", ")
            : "Not specified";
          const isExpanded = expandedOrders[order.orderId] ?? false;
          const orderStatus = getOrderStatus(order);
          const orderStatusLabel =
            formatStatusLabel(orderStatus) || orderStatus || "Unknown";
          const orderStatusChipColor = getOrderStatusChipColor(orderStatus);
          const orderLocationCandidate = resolveOrderLocation(order, shipmentLocation);
          const orderHasLocation =
            typeof orderLocationCandidate.lat === "number" &&
            typeof orderLocationCandidate.lng === "number";
          const orderTooltip = onOpenOrderMap
            ? orderHasLocation
              ? "Open map view"
              : "View details (location pending)"
            : "Map unavailable";
          const orderMapDisabled = !onOpenOrderMap || !orderHasLocation;

          return (
            <Box key={order.orderId} sx={{ mb: 1.5 }}>
              <ListItem
                sx={{
                  bgcolor: isExpanded
                    ? muiTheme.palette.action.hover
                    : muiTheme.palette.background.paper,
                  borderRadius: 1,
                  border: `1px solid ${muiTheme.palette.divider}`,
                  mb: 0.5,
                }}
                onClick={() => handleToggleOrder(order.orderId)}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleToggleOrder(order.orderId);
                    }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" color="text.primary">
                        Order {stripOrderPrefix(order.orderId, order.snapshot)}
                      </Typography>
                      <Chip
                        size="small"
                        color={orderStatusChipColor}
                        label={orderStatusLabel}
                      />
                      <Chip size="small" label={`${totalItems} items`} />
                      <Tooltip title={orderTooltip}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOrderMapClick(order);
                            }}
                            disabled={orderMapDisabled}
                            sx={{ ml: 0.5 }}
                          >
                            <RoomIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  }
                  secondary={
                    <Box sx={{ mt: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">
                        Dealer: {order.dealerLabel}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                        <Chip
                          size="small"
                          label={`Included: ${order.includedItems.length}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Excluded: ${order.excludedItems.length}`}
                          color="warning"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.75}
                      >
                        Destinations: {destinationsLabel}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    borderLeft: `1px solid ${muiTheme.palette.divider}`,
                    ml: 3,
                    pl: 2,
                    py: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Included Items
                    </Typography>
                    <List disablePadding>
                      {order.includedItems.map((item) => {
                        const itemLocationCandidate = resolveItemLocation(
                          item,
                          order,
                          shipmentLocation
                        );
                        const itemHasLocation =
                          typeof itemLocationCandidate.lat === "number" &&
                          typeof itemLocationCandidate.lng === "number";
                        const itemTooltip = onOpenItemMap
                          ? itemHasLocation
                            ? "Open map view"
                            : "View details (location pending)"
                          : "Map unavailable";

                        return (
                          <ListItem
                            key={item.id}
                            sx={{
                              py: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <Inventory2Icon
                              fontSize="small"
                              sx={{ color: "text.secondary", mr: 1, mt: 0.5 }}
                            />
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    alignItems="center"
                                  >
                                    {item.cabinetNumber && (
                                      <Typography variant="body2" fontWeight={700}>
                                        Cabinet # {item.cabinetNumber}
                                      </Typography>
                                    )}
                                    <Chip
                                      size="small"
                                      label={
                                        formatStatusLabel(item.status) ||
                                        item.status ||
                                        ""
                                      }
                                      color={getItemStatusChipColor(item.status)}
                                    />
                                    <Tooltip title={itemTooltip}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleItemMapClick(order, item, "included");
                                          }}
                                          disabled={!onOpenItemMap || !itemHasLocation}
                                          sx={{ ml: 0.5 }}
                                        >
                                          <RoomIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.description || item.modelName || item.id}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {/* Item ID removed from UI per request */}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Destination: {item.destination || "Not provided"}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                      {order.includedItems.length === 0 && (
                        <Box sx={{ py: 1.5, px: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No included items for this order.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Excluded Items
                    </Typography>
                    <List disablePadding>
                      {order.excludedItems.map((item) => {
                        const itemLocationCandidate = resolveItemLocation(
                          item,
                          order,
                          shipmentLocation
                        );
                        const itemHasLocation =
                          typeof itemLocationCandidate.lat === "number" &&
                          typeof itemLocationCandidate.lng === "number";
                        const itemTooltip = onOpenItemMap
                          ? itemHasLocation
                            ? "Open map view"
                            : "View details (location pending)"
                          : "Map unavailable";

                        return (
                          <ListItem
                            key={item.id}
                            sx={{
                              py: 1,
                              alignItems: "flex-start",
                            }}
                            secondaryAction={
                              <Tooltip title={itemTooltip}>
                                <span>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() =>
                                      handleItemMapClick(order, item, "excluded")
                                    }
                                    disabled={!onOpenItemMap || !itemHasLocation}
                                  >
                                    <RoomIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            }
                          >
                            <Inventory2Icon
                              fontSize="small"
                              sx={{ color: "text.secondary", mr: 1, mt: 0.5 }}
                            />
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    alignItems="center"
                                  >
                                    {item.cabinetNumber && (
                                      <Typography variant="body2" fontWeight={700}>
                                        Cabinet # {item.cabinetNumber}
                                      </Typography>
                                    )}
                                    <Chip
                                      size="small"
                                      label={
                                        formatStatusLabel(item.status) ||
                                        item.status ||
                                        ""
                                      }
                                      color={getItemStatusChipColor(item.status)}
                                    />
                                    <Tooltip title={itemTooltip}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleItemMapClick(order, item, "excluded");
                                          }}
                                          disabled={!onOpenItemMap || !itemHasLocation}
                                          sx={{ ml: 0.5 }}
                                        >
                                          <RoomIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.description || item.modelName || item.id}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {/* Item ID removed from UI per request */}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Destination: {item.destination || "Not provided"}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                      {order.excludedItems.length === 0 && (
                        <Box sx={{ py: 1.5, px: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No excluded items for this order.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Box>
                </Box>
              </Collapse>
              <Divider />
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

const ViewShipments: React.FC = () => {
  const { shipments, dateRange, setDateRange } = useShipmentManager();
  const { shipments: dealerShipments } = usePrimaryFetchedData();
  const userData = useUserData();
  const userEmail = (userData as any)?.email as string | undefined;
  const { users } = useUsersByManufacturer();
  const muiTheme = useMuiTheme();
  const { themeVariant } = useThemes();
  const mapStyles = useMemo(
    () => (themeVariant.mode === "dark" ? darkMapStyle : lightMapStyle),
    [themeVariant.mode]
  );
  const isDealer = userData?.role === "dealer";
  const baseShipments = useMemo(() => {
    if (isDealer && Array.isArray(dealerShipments)) {
      return dealerShipments as Shipment[];
    }
    return shipments ?? [];
  }, [isDealer, dealerShipments, shipments]);
  const filteredShipments = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return baseShipments;
    const start = dateRange.startDate;
    const end = dateRange.endDate;
    return baseShipments.filter((shipment) => {
      const shipmentDate = getShipmentDate(shipment);
      if (!shipmentDate) return false;
      return shipmentDate >= start && shipmentDate <= end;
    });
  }, [baseShipments, dateRange]);

  const { locations, pending: geoPending } = useShipmentGeocodes({
    shipments: filteredShipments,
  });
  const [mapDialogState, setMapDialogState] = useState<MapDialogState>({
    open: false,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    try {
      const sampleIds = filteredShipments.slice(0, 3).map((s) => s?.id ?? null);
      console.debug("[DealerDebug][ViewShipments] render summary", {
        role: userData?.role,
        dealerId: userData?.dealerId,
        email: userEmail,
        isAdmin: userData?.isAdmin,
        source: isDealer ? "dealerShipments" : "managerShipments",
        shipmentsCount: filteredShipments.length,
        sampleIds,
        dateRangeStart: dateRange?.startDate?.toISOString?.(),
        dateRangeEnd: dateRange?.endDate?.toISOString?.(),
      });
    } catch (error) {
      console.warn("[DealerDebug][ViewShipments] failed to log summary", error);
    }
  }, [filteredShipments, userData?.role, userData?.dealerId, userEmail, dateRange, isDealer]);

  const formatRange = () => {
    const { startDate, endDate } = dateRange || {} as any;
    if (!startDate || !endDate) return "Select Date Range";
    try {
      return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
    } catch {
      return "Select Date Range";
    }
  };

  const driverNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    (users || []).forEach((user) => {
      const email = user.email ?? "";
      if (!email) return;
      const rawName = (user.name || "").trim();
      if (!rawName) {
        lookup[email] = email;
        return;
      }
      const parts = rawName.split(/\s+/);
      let display = rawName;
      if (parts.length >= 2) {
        display = `${parts[0]} ${parts[parts.length - 1]}`;
      }
      lookup[email] = display;
    });
    return lookup;
  }, [users]);

  const mapDialogData = useMemo<ActiveDialogData | null>(() => {
    if (!mapDialogState.open) return null;
    const { context } = mapDialogState;
    const shipmentLocation = locations[context.shipment.id]
      ? {
          lat: locations[context.shipment.id].lat,
          lng: locations[context.shipment.id].lng,
          formattedAddress: locations[context.shipment.id].formattedAddress,
        }
      : undefined;
    const ordersForShipment = collectOrderDisplayData(context.shipment);

    if (context.kind === "shipment") {
      const shipmentData = buildShipmentDialogData(
        context.shipment,
        ordersForShipment,
        shipmentLocation
      );
      return {
        kind: "shipment",
        title: `Shipment ${context.shipment.id}`,
        subtitle: shipmentData.primaryAddress,
        mapShipments: [shipmentData.mapShipment],
        shipment: context.shipment,
        orders: ordersForShipment,
        totalItems: shipmentData.totalItems,
        uniqueDestinations: shipmentData.uniqueDestinations,
        primaryAddress: shipmentData.primaryAddress,
        driverList: shipmentData.driverList,
      };
    }

    const resolvedOrder =
      ordersForShipment.find((order) => order.orderId === context.order.orderId) ??
      context.order;

    if (context.kind === "order") {
      const orderData = buildOrderDialogData(
        context.shipment,
        resolvedOrder,
        shipmentLocation
      );
      const statusKey =
        context.statusKey ?? normalizeStatusValue(getOrderStatus(resolvedOrder));
      return {
        kind: "order",
        title: `Order ${stripOrderPrefix(resolvedOrder.orderId, resolvedOrder.snapshot)}`,
        subtitle: orderData.addressSummary,
        mapShipments: [orderData.mapShipment],
        shipment: context.shipment,
        order: resolvedOrder,
        addressSummary: orderData.addressSummary,
        statusKey,
      };
    }

    if (context.kind === "item") {
      const matchedItem =
        resolvedOrder.includedItems.find((item) => item.id === context.item.id) ??
        resolvedOrder.excludedItems.find((item) => item.id === context.item.id) ??
        context.item;
      const itemData = buildItemDialogData(
        context.shipment,
        resolvedOrder,
        matchedItem,
        shipmentLocation
      );
      const rawHistory = Array.isArray((matchedItem as any)?.scanHistory)
        ? ((matchedItem as any).scanHistory as ItemHistoryEntry[])
        : [];
      return {
        kind: "item",
        title: itemData.mapShipment.label || `Item ${matchedItem.id}`,
        subtitle: itemData.addressSummary,
        mapShipments: [itemData.mapShipment],
        shipment: context.shipment,
        order: resolvedOrder,
        item: matchedItem,
        addressSummary: itemData.addressSummary,
        itemType: context.itemType,
        statusKey:
          context.statusKey ?? normalizeStatusValue(matchedItem.status),
        history: rawHistory,
      };
    }

    return null;
  }, [mapDialogState, locations]);

  const closeMapDialog = useCallback(() => {
    setMapDialogState({ open: false });
  }, [setMapDialogState]);

  const handleOpenShipmentMap = useCallback(
    (shipment: Shipment) => {
      const geoRecord = locations[shipment.id];
      console.log("[ViewShipments] Opening shipment map dialog", {
        shipmentId: shipment.id,
        hasGeoRecord: Boolean(geoRecord),
        lat: geoRecord?.lat,
        lng: geoRecord?.lng,
      });
      setMapDialogState({
        open: true,
        context: { kind: "shipment", shipment },
      });
    },
    [locations, setMapDialogState]
  );

  const handleOpenOrderMap = useCallback(
    (shipment: Shipment, order: OrderDisplayData) => {
      const geoRecord = locations[shipment.id];
      const shipmentLocation = geoRecord
        ? {
            lat: geoRecord.lat,
            lng: geoRecord.lng,
            formattedAddress: geoRecord.formattedAddress,
          }
        : undefined;
      const orderLocation = resolveOrderLocation(order, shipmentLocation);
      const orderStatus = getOrderStatus(order);
      const statusKey = normalizeStatusValue(orderStatus);
      console.log("[ViewShipments] Opening order map dialog", {
        shipmentId: shipment.id,
        orderId: order.orderId,
        hasShipmentGeo: Boolean(geoRecord),
        orderLat: orderLocation.lat,
        orderLng: orderLocation.lng,
      });
      setMapDialogState({
        open: true,
        context: { kind: "order", shipment, order, statusKey },
      });
    },
    [locations, setMapDialogState]
  );

  const handleOpenItemMap = useCallback(
    (
      shipment: Shipment,
      order: OrderDisplayData,
      item: ItemSnapshot,
      itemType: "included" | "excluded"
    ) => {
      const geoRecord = locations[shipment.id];
      const shipmentLocation = geoRecord
        ? {
            lat: geoRecord.lat,
            lng: geoRecord.lng,
            formattedAddress: geoRecord.formattedAddress,
          }
        : undefined;
      const itemLocation = resolveItemLocation(item, order, shipmentLocation);
      const statusKey = normalizeStatusValue(item.status);
      console.log("[ViewShipments] Opening item map dialog", {
        shipmentId: shipment.id,
        orderId: order.orderId,
        itemId: item.id,
        itemType,
        hasShipmentGeo: Boolean(geoRecord),
        itemLat: itemLocation.lat,
        itemLng: itemLocation.lng,
      });
      setMapDialogState({
        open: true,
        context: { kind: "item", shipment, order, item, itemType, statusKey },
      });
    },
    [locations, setMapDialogState]
  );

  const renderDialogInfo = () => {
    if (!mapDialogData) return null;
    switch (mapDialogData.kind) {
      case "shipment":
        return (
          <ShipmentInfoPanel
            data={mapDialogData}
            driverNameLookup={driverNameLookup}
            onOpenOrderMap={(order) => handleOpenOrderMap(mapDialogData.shipment, order)}
          />
        );
      case "order":
        return (
          <OrderInfoPanel
            data={mapDialogData}
            onOpenItemMap={(item, itemType) =>
              handleOpenItemMap(
                mapDialogData.shipment,
                mapDialogData.order,
                item,
                itemType
              )
            }
          />
        );
      case "item":
        return <ItemInfoPanel data={mapDialogData} />;
      default:
        return null;
    }
  };

  const dialogShipments = mapDialogData?.mapShipments ?? [];
  const mapShipmentsWithCoords = useMemo(() => {
    const filtered = dialogShipments.filter(
      (entry) =>
        Number.isFinite(entry.lat) && Number.isFinite(entry.lng)
    );
    if (mapDialogState.open) {
      console.log("[ViewShipments] mapShipmentsWithCoords", {
        count: filtered.length,
        ids: filtered.map((entry) => entry.id),
        rawCount: dialogShipments.length,
      });
    }
    return filtered;
  }, [dialogShipments, mapDialogState.open]);
  const hasCoordinates = mapShipmentsWithCoords.length > 0;
  const selectedShipmentIdForMap = hasCoordinates
    ? mapShipmentsWithCoords[0]?.id ?? null
    : null;
  const dialogLoading = mapDialogData?.kind === "shipment" ? geoPending : false;

  useEffect(() => {
    if (mapDialogState.open && mapShipmentsWithCoords.length === 0) {
      console.warn("[ViewShipments] Map dialog has no coordinate-enabled entries", {
        context: mapDialogData?.kind,
        shipmentId: mapDialogData?.shipment.id,
        rawCount: dialogShipments.length,
      });
    }
  }, [
    mapDialogState.open,
    mapShipmentsWithCoords.length,
    mapDialogData,
    dialogShipments.length,
  ]);

  useEffect(() => {
    if (mapDialogState.open && mapDialogData && !hasCoordinates) {
      console.warn("[ViewShipments] Map dialog opened without coordinates", {
        context: mapDialogData.kind,
        title: mapDialogData.title,
      });
    }
  }, [mapDialogState.open, mapDialogData, hasCoordinates]);

  const columns = useMemo<MRT_ColumnDef<Shipment>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Shipment ID",
        size: 160,
        minSize: 140,
        maxSize: 220,
        muiTableHeadCellProps: { sx: { px: 0.75, py: 0.5, whiteSpace: "nowrap" } },
        muiTableBodyCellProps: { sx: { px: 0.75, py: 0.9, whiteSpace: "nowrap" } },
      },
      {
        header: "Date",
        size: 120,
        minSize: 110,
        maxSize: 140,
        accessorFn: (row: Shipment) =>
          row.shipmentDate && (row.shipmentDate as any).toDate
            ? (row.shipmentDate as any).toDate().toLocaleDateString()
            : "",
        muiTableHeadCellProps: { sx: { px: 0.75, py: 0.5, whiteSpace: "nowrap" } },
        muiTableBodyCellProps: { sx: { px: 0.75, py: 0.9, whiteSpace: "nowrap" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        minSize: 110,
        maxSize: 150,
        muiTableHeadCellProps: { sx: { px: 0.75, py: 0.5, whiteSpace: "nowrap" } },
        muiTableBodyCellProps: { sx: { px: 0.75, py: 0.9, whiteSpace: "nowrap" } },
        Cell: ({ cell }) => {
          const raw = cell.getValue<string | null | undefined>();
          const safeStatus = typeof raw === "string" ? raw : undefined;
          const label = formatStatusLabel(safeStatus) || safeStatus || "";
          const color = getShipmentStatusChipColor(safeStatus);
          return <Chip size="small" color={color} label={label} />;
        },
      },
      {
        header: "Orders",
        size: 100,
        minSize: 90,
        maxSize: 130,
        accessorFn: (row: Shipment) => Object.keys(row.ordersSnapshot || {}).length,
        muiTableHeadCellProps: {
          sx: { px: 0.6, py: 0.5, whiteSpace: "nowrap" },
        },
        muiTableBodyCellProps: {
          sx: { px: 0.6, py: 0.9, whiteSpace: "nowrap" },
        },
      },
      {
        header: "Items",
        size: 110,
        minSize: 90,
        maxSize: 140,
        accessorFn: (row: Shipment) =>
          Object.values(row.ordersSnapshot || {}).reduce((acc, orderSnap) => {
            const os = orderSnap as OrderSnapshot;
            return acc + Object.keys(os.includedItemsSnapshot || {}).length;
          }, 0),
        muiTableHeadCellProps: {
          sx: { px: 0.6, py: 0.5, whiteSpace: "nowrap" },
        },
        muiTableBodyCellProps: {
          sx: { px: 0.6, py: 0.9, whiteSpace: "nowrap" },
        },
      },
      {
        header: "Drivers",
        size: 280,
        minSize: 240,
        maxSize: 420,
        accessorFn: (row: Shipment) =>
          (row.drivers || [])
            .map((driverId) => driverNameLookup[driverId] || driverId)
            .join(", "),
        muiTableHeadCellProps: { sx: { px: 0.75, py: 0.5 } },
        muiTableBodyCellProps: { sx: { px: 0.75, py: 0.9 } },
      },
      {
        accessorKey: "truckId",
        header: "Truck",
        size: 140,
        muiTableHeadCellProps: { sx: { px: 0.6, py: 0.5, whiteSpace: "nowrap" } },
        muiTableBodyCellProps: { sx: { px: 0.6, py: 0.9, whiteSpace: "nowrap" } },
      },
      {
        id: "map",
        header: "Map",
        size: 70,
        maxSize: 90,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          sx: { px: 0.5, py: 0.5, textAlign: "center" },
        },
        muiTableBodyCellProps: {
          sx: { px: 0.5, py: 0.5 },
        },
        Cell: ({ row }) => {
          const shipment = row.original as Shipment;
          const locationRecord = locations[shipment.id];
          const hasLocation =
            locationRecord &&
            Number.isFinite(locationRecord.lat) &&
            Number.isFinite(locationRecord.lng);
          return (
            <Tooltip title={hasLocation ? "Open map view" : "Map unavailable"}>
              <span>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenShipmentMap(shipment);
                  }}
                  disabled={!hasLocation}
                >
                  <RoomIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [driverNameLookup, handleOpenShipmentMap, locations]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredShipments,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableBottomToolbar: true,
    positionToolbarAlertBanner: "none",
    enableExpanding: true,
    enableExpandAll: false,
    enableRowSelection: false,
    enableRowActions: false,
    renderDetailPanel: ({ row }) => {
      const shipment = row.original as Shipment;
      const locationRecord = locations[shipment.id];
      const shipmentLocation = locationRecord
        ? {
            lat: locationRecord.lat,
            lng: locationRecord.lng,
            formattedAddress: locationRecord.formattedAddress,
          }
        : undefined;
      return (
        <OrdersDetailPanel
          shipment={shipment}
          shipmentLocation={shipmentLocation}
          onOpenOrderMap={({ shipment: payloadShipment, order }) =>
            handleOpenOrderMap(payloadShipment, order)
          }
          onOpenItemMap={({ shipment: payloadShipment, order, item, itemType }) =>
            handleOpenItemMap(payloadShipment, order, item, itemType)
          }
        />
      );
    },
    initialState: { density: "spacious", pagination: { pageSize: 50, pageIndex: 0 } },
    getRowId: (row) => row.id,
    muiTableProps: {
      sx: () => ({
        elevation: 0,
        boxShadow: "none",
      }),
    },
    muiTableBodyProps: {
      sx: {
        backgroundColor: muiTheme.palette.background.paper,
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: muiTheme.palette.background.paper,
        "& th": {
          borderBottom: `1px solid ${muiTheme.palette.divider} !important`,
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => row.toggleExpanded(),
      sx: {
        backgroundColor: muiTheme.palette.background.paper,
        "&:hover": {
          cursor: "pointer",
          backgroundColor: muiTheme.palette.action.hover,
        },
        "& td": {
          borderBottom: `1px solid ${muiTheme.palette.divider} !important`,
        },
        "& td:after": {
          backgroundColor: "transparent !important",
        },
      },
    }),
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: muiTheme.palette.background.paper,
      },
    },
    muiTopToolbarProps: {
      sx: (t) => ({
        display: "flex",
        alignItems: "center",
        backgroundColor: t.palette.background.paper,
        "& > div": { flexDirection: "row-reverse" },
      }),
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '',
        muiTableHeadCellProps: {
          sx: { px: 0.75, py: 0.5, whiteSpace: 'nowrap', verticalAlign: 'middle' },
        },
        muiTableBodyCellProps: {
          sx: { px: 0.75, py: 0.9, verticalAlign: 'middle' },
        },
      },
    },
    renderTopToolbarCustomActions: () => (
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CalendarMonthIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          backgroundColor: "background.paper",
          borderColor: "divider",
          color: "primary.main",
          position: "relative",
          bottom: ".25rem",
          "&:hover, &:active": {
            backgroundColor: "background.paper",
            borderColor: "primary.main",
          },
        }}
      >
        {formatRange()}
      </Button>
    ),
  });

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Box sx={{ width: { xs: "100%", lg: "100%" }, maxWidth: "1400px" }}>
          <Box
            sx={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "primary.main",
              }}
            >
              <LocalShippingIcon
                sx={{ fontSize: "2.5rem", bottom: "2px", position: "relative" }}
                color="primary"
              />
              View Shipments
            </Typography>
          </Box>

          <MaterialReactTable table={table} />

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <CalendarWrapper>
              <DateRangePicker
                onChange={(item: any) => {
                  const range = item.range1 || item.selection || {};
                  setDateRange({
                    startDate: range.startDate ?? dateRange.startDate,
                    endDate: range.endDate ?? dateRange.endDate,
                    key: range.key || "selection",
                  });
                }}
                showPreview={false}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={[dateRange]}
                showDateDisplay={false}
                staticRanges={[]}
                inputRanges={[]}
                direction="horizontal"
              />
            </CalendarWrapper>
          </Popover>

          <Dialog
            open={mapDialogState.open && Boolean(mapDialogData)}
            onClose={closeMapDialog}
            maxWidth="lg"
            fullWidth
            aria-labelledby="shipment-map-dialog-title"
          >
            {mapDialogData ? (
              <>
                <DialogTitle
                  id="shipment-map-dialog-title"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {mapDialogData.title}
                    </Typography>
                    {mapDialogData.subtitle && (
                      <Typography variant="body2" color="text.secondary">
                        {mapDialogData.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={closeMapDialog}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    sx={{ minHeight: { xs: 520, md: 480 } }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: { xs: 320, md: 480 },
                        position: "relative",
                      }}
                    >
                      {hasCoordinates ? (
                        <ShipmentsMap
                          shipments={mapShipmentsWithCoords}
                          selectedShipmentId={selectedShipmentIdForMap}
                          mapStyles={mapStyles}
                          isLoading={dialogLoading}
                          initialZoom={14}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            p: 3,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No location data available for this selection.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: { xs: "100%", md: 360 },
                        borderTop: { xs: 1, md: 0 },
                        borderLeft: { md: 1 },
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        p: 3,
                      }}
                    >
                      {renderDialogInfo()}
                    </Box>
                  </Stack>
                </DialogContent>
              </>
            ) : null}
          </Dialog>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ViewShipments;



